import { vec3 } from 'gl-matrix';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { ContourTexture } from '../../../data/structure/ContourTexture';
import { RegionOfInterest } from '../../../data/structure/RegionOfInterest';
import { applyAsymmetricMarginCpu } from './AsymmetricMarginConvolution';
import { applyAsymmetricMarginGpuHierarchical } from './AsymmetricMarginGL';
import { SDFOperationRenderer } from './BooleanRenderer';
import { gaussianSmooth3DUint8, getSigmaForLevel } from './GaussianSmoothing';
import { SdfGenerator } from './SdfGenerator';
import { fillCavities, removeSmallParts } from './SDFPostProcessing';
import { quantizeSdfMmToUint8 } from './SDFPropagation';
import { GlOperationGuard } from './utils';

export enum BooleanOperation {
    OR = 'or',
    AND = 'and',
    XOR = 'xor',
    AND_NOT = 'not',
}

/**
 * Boolean and morphological operations on SDF-based
 * {@link ContourTexture ContourTextures}. Use {@link applyBoolean}
 * to write a result into an existing destination (either a
 * {@link RegionOfInterest}'s texture or a {@link ContourTexture}
 * the caller owns). Use {@link applyBooleanToNewTexture} when no
 * destination exists yet.
 *
 * Internally each operation is implemented by a private
 * `(dest, other)` subfunction that mutates `dest` in place.
 * `applyBoolean` decides whether one of the source textures is
 * already the destination — in which case it dispatches in place
 * with no allocation — or whether a fresh texture must be built.
 */
export class SDFOperations {
    /**
     * Apply a boolean operation `src1 OP src2` and write the
     * result into `destination` (an ROI or a caller-owned
     * {@link ContourTexture}).
     *
     * In-place fast path is used when one of the sources IS the
     * destination's texture:
     *   - `src1 === destTexture`: dispatch `op(destTexture, src2)`.
     *   - `src2 === destTexture` and op is commutative
     *     (OR/AND/XOR): dispatch `op(destTexture, src1)`.
     * Otherwise a fresh texture is allocated, seeded from src1,
     * mutated by the subfunction, then either swapped into the
     * ROI (if `destination` is an ROI) or copied into the
     * caller's ContourTexture via `resetSdfAndGrid`.
     */
    public static applyBoolean(
        src1: ContourTexture,
        src2: ContourTexture,
        operation: BooleanOperation,
        destination: RegionOfInterest | ContourTexture
    ) {
        const isROI = destination instanceof RegionOfInterest;
        if (isROI) {
            destination.assertHasTexture();
        }
        const destTex: ContourTexture = isROI
            ? destination.texture!
            : destination;

        // Path:
        //   src1 === destTex                          → in-place
        //   src2 === destTex && op ∈ {OR, AND, XOR}   → in-place,
        //                                               swap args
        //   otherwise                                 → allocate
        let target: ContourTexture;
        let other: ContourTexture;
        let allocated = false;
        if (src1 === destTex) {
            target = destTex;
            other = src2;
        } else if (src2 === destTex && operation !== BooleanOperation.AND_NOT) {
            target = destTex;
            other = src1;
        } else {
            target = src1.unionAligned(src1);
            RenderStorage.getRenderer(SDFOperationRenderer).renderOnDestination(
                target,
                BooleanOperation.OR,
                src1
            );
            other = src2;
            allocated = true;
        }

        switch (operation) {
            case BooleanOperation.OR:
                this.or(target, other);
                break;
            case BooleanOperation.AND:
                this.and(target, other);
                break;
            case BooleanOperation.AND_NOT:
                this.andNot(target, other);
                break;
            case BooleanOperation.XOR:
                this.xor(target, other);
                break;
            default:
                throw new Error(`Unknown boolean operation: ${operation}`);
        }

        if (allocated) {
            if (isROI) {
                destination.texture!.delete();
                destination.texture = target;
            } else {
                // ContourTexture destination — preserve the caller's
                // reference; copy target's SDF into it via readback.
                destination.resetSdfAndGrid(
                    target.signedDistanceField.toArray(),
                    target.textureGrid
                );
                target.delete();
            }
        }

        const finalTex: ContourTexture = isROI
            ? destination.texture!
            : destination;
        finalTex.cropToContentBounds();
    }

    /**
     * Apply a boolean operation on two source textures and return
     * the result as a new {@link ContourTexture}. No
     * {@link RegionOfInterest} is modified. The caller owns the
     * returned texture and must call {@link ContourTexture.delete}
     * when done.
     *
     * Allocates a fresh result texture (cloned from src1) and
     * delegates to {@link applyBoolean}. Passing the freshly
     * cloned texture as both `src1` and `destination` makes
     * applyBoolean take its in-place fast path, so no second
     * allocation happens.
     */
    public static applyBooleanToNewTexture(
        src1: ContourTexture,
        src2: ContourTexture,
        operation: BooleanOperation
    ): ContourTexture {
        const result = src1.unionAligned(src1);
        RenderStorage.getRenderer(SDFOperationRenderer).renderOnDestination(
            result,
            BooleanOperation.OR,
            src1
        );
        this.applyBoolean(result, src2, operation, result);
        return result;
    }

    /**
     * In-place OR: `dest = dest ∪ other`. Expands `dest`'s grid
     * if `other` extends beyond it.
     */
    private static or(dest: ContourTexture, other: ContourTexture): void {
        const renderer = RenderStorage.getRenderer(SDFOperationRenderer);
        if (dest.fullBoundingBox.contains(other.fullBoundingBox)) {
            renderer.renderOnDestination(dest, BooleanOperation.OR, other);
            return;
        }
        // Expand dest to cover the union via a temp + readback.
        const temp = dest.unionAligned(other);
        renderer.renderOnDestination(temp, BooleanOperation.OR, dest, other);
        dest.resetSdfAndGrid(
            temp.signedDistanceField.toArray(),
            temp.textureGrid
        );
        temp.delete();
    }

    /**
     * In-place AND: `dest = dest ∩ other`.
     *
     * Because {@link SDFOperationRenderer.renderOnDestination} only
     * touches destination pixels covered by the source's cross-section,
     * an AND with a source whose grid is smaller than `dest`'s would
     * leave dest's pre-AND content visible in the uncovered region —
     * wrong for true intersection. To avoid that, build a temp source
     * sized to cover `dest`'s grid (via `unionAligned`), OR `other`
     * into it (leaving 0 outside `other`'s bounds), then render AND
     * with the temp as source. The 0 regions then correctly clamp
     * dest to 0 via `gl.MIN`.
     */
    private static and(dest: ContourTexture, other: ContourTexture): void {
        const renderer = RenderStorage.getRenderer(SDFOperationRenderer);
        if (other.fullBoundingBox.contains(dest.fullBoundingBox)) {
            // `other` already covers every voxel of `dest` — direct AND.
            renderer.renderOnDestination(dest, BooleanOperation.AND, other);
            return;
        }
        // Expand the source so it covers every voxel of dest. The
        // temp is initialised to 0; OR-ing `other` in places its
        // content where it belongs and leaves 0 in the regions
        // beyond `other`'s grid, which is exactly what AND needs.
        const temp = dest.unionAligned(other);
        renderer.renderOnDestination(temp, BooleanOperation.OR, other);
        renderer.renderOnDestination(dest, BooleanOperation.AND, temp);
        temp.delete();
    }

    /** In-place AND_NOT: `dest = dest \ other`. */
    private static andNot(dest: ContourTexture, other: ContourTexture): void {
        const renderer = RenderStorage.getRenderer(SDFOperationRenderer);
        renderer.renderOnDestination(dest, BooleanOperation.AND_NOT, other);
    }

    /**
     * In-place XOR: `dest = (dest ∪ other) \ (dest ∩ other)`.
     * Snapshots `dest ∩ other` into a temp before mutating dest,
     * then unions other into dest, then subtracts the snapshot.
     */
    private static xor(dest: ContourTexture, other: ContourTexture): void {
        const renderer = RenderStorage.getRenderer(SDFOperationRenderer);

        // andTemp = dest ∩ other, captured before dest is mutated.
        const andTemp = ContourTexture.createIntersectionAligned(dest, other);
        try {
            if (andTemp) {
                renderer.renderOnDestination(
                    andTemp,
                    BooleanOperation.OR,
                    dest
                );
                renderer.renderOnDestination(
                    andTemp,
                    BooleanOperation.AND,
                    other
                );
            }

            this.or(dest, other);

            if (andTemp) {
                renderer.renderOnDestination(
                    dest,
                    BooleanOperation.AND_NOT,
                    andTemp
                );
            }
        } finally {
            andTemp?.delete();
        }
    }

    /**
     * Compute a ring structure as the boolean difference of two
     * symmetric margins applied to the source surface:
     *   ring = margin(src, upperMm) AND_NOT margin(src, lowerMm)
     *
     * Both boundaries are signed distances from the surface:
     *   positive = expand outward, negative = contract inward,
     *   0 = the original surface.
     *
     * @param src Source contour texture with valid SDF
     * @param lowerMarginMm Lower (inner) boundary in mm, signed
     * @param upperMarginMm Upper (outer) boundary in mm, signed;
     *        must be strictly greater than lowerMarginMm
     * @returns New ContourTexture containing the ring region
     */
    public static async applyRing(
        src: ContourTexture,
        lowerMarginMm: number,
        upperMarginMm: number
    ): Promise<ContourTexture> {
        if (upperMarginMm <= lowerMarginMm) {
            throw new Error(
                `Upper margin (${upperMarginMm}mm) must be ` +
                    `greater than lower margin (${lowerMarginMm}mm)`
            );
        }

        const upperTexture =
            upperMarginMm !== 0
                ? await this.applySymmetricMargin(src, upperMarginMm)
                : src;

        const lowerTexture =
            lowerMarginMm !== 0
                ? await this.applySymmetricMargin(src, lowerMarginMm)
                : src;

        // ring = upper AND_NOT lower
        // Preserve the source margin so SDF falloff quality is maintained
        // in the returned texture for downstream visualization and ops.
        const marginMm = src.margin ?? ContourTexture.DEFAULT_MARGIN;
        const renderer = RenderStorage.getRenderer(SDFOperationRenderer);
        const temp = upperTexture.unionAligned(lowerTexture, marginMm);
        renderer.renderOnDestination(temp, BooleanOperation.OR, upperTexture);
        renderer.renderOnDestination(
            temp,
            BooleanOperation.AND_NOT,
            lowerTexture
        );

        // Clean up intermediate textures (skip if we reused src)
        if (upperMarginMm !== 0) {
            (upperTexture as ContourTexture).delete();
        }
        if (lowerMarginMm !== 0) {
            (lowerTexture as ContourTexture).delete();
        }

        return temp;
    }

    public static async applySymmetricMargin(
        src: ContourTexture,
        marginMm: number
    ): Promise<ContourTexture> {
        const absMargin = Math.abs(marginMm);
        return this.applyMarginBase(
            src,
            marginMm,
            absMargin,
            (texture, falloff) =>
                SDFOperations.applySymmetricShift(
                    texture.signedDistanceField!.toArray(),
                    marginMm,
                    falloff
                )
        );
    }

    /**
     * Builds the padded "expanded" SDF that {@link applyAsymmetricMargin}
     * reads from: the source SDF re-rendered into a grid grown by
     * `maxBufferMm` on every side so the margin shift has room. The
     * expanded grid depends only on the source and buffer width, so the
     * caller builds it once and reuses it across previews and applies;
     * the caller also owns its lifetime and must `delete()` it.
     *
     * @param src Source contour texture with valid SDF
     * @param maxBufferMm Largest per-direction margin (mm) the expanded
     *        grid must accommodate
     * @returns New expanded ContourTexture
     */
    public static createAsymmetricExpanded(
        src: ContourTexture,
        maxBufferMm: number
    ): ContourTexture {
        if (!src.signedDistanceField) {
            throw new Error('Cannot apply margin: source has no SDF data');
        }
        if (src.signedDistanceField.size[2] === 0) {
            throw new Error('Cannot apply margin: source SDF is empty');
        }

        const expanded = ContourTexture.createNewWithMargin(maxBufferMm, src);
        const renderer = RenderStorage.getRenderer(SDFOperationRenderer);
        renderer.renderOnDestination(expanded, BooleanOperation.OR, src);
        return expanded;
    }

    /**
     * Applies asymmetric margins to a pre-expanded SDF. Each LPS axis
     * can have independent positive and negative margins.
     *
     * Runs the GPU morphological dilation/erosion (max/min of the
     * SDF over an asymmetric ellipsoidal kernel) against the expanded
     * grid, falling back to the CPU implementation if the GPU path
     * fails.
     *
     * @param expanded Padded source SDF from
     *        {@link createAsymmetricExpanded}, sized to fit the margins
     * @param negLpsMarginMm Margins for negative LPS
     *        directions: [Right, Anterior, Inferior] mm
     * @param posLpsMarginMm Margins for positive LPS
     *        directions: [Left, Posterior, Superior] mm
     * @returns New ContourTexture with margins applied
     */
    public static async applyAsymmetricMargin(
        expanded: ContourTexture,
        negLpsMarginMm: vec3,
        posLpsMarginMm: vec3
    ): Promise<ContourTexture> {
        if (!expanded.signedDistanceField) {
            throw new Error('Cannot apply margin: source has no SDF data');
        }

        // Allocate the output texture up front so the GPU pipeline
        // can render directly into its SDF — no CPU round trip.
        const output = new ContourTexture(
            expanded.grid,
            expanded.signedDistanceField.size,
            expanded.textureGrid.getPosition(),
            expanded.margin
        );
        output.resetGrid(expanded.textureGrid);

        try {
            await applyAsymmetricMarginGpuHierarchical(
                expanded,
                negLpsMarginMm,
                posLpsMarginMm,
                output
            );
        } catch (e) {
            const msg =
                e instanceof Error
                    ? `${e.message}\n${e.stack ?? ''}`
                    : String(e);
            console.warn(
                'GPU asymmetric margin failed, falling back to CPU: ' + msg
            );
            const resultPixels = applyAsymmetricMarginCpu(
                expanded.signedDistanceField!.toArray(),
                expanded.textureGrid,
                negLpsMarginMm,
                posLpsMarginMm
            );
            output.resetSdfData(resultPixels);
        }

        // Caller is responsible for cropToImageBounds when the
        // result is being applied. Preview does not need it; the
        // renderer clips against image bounds at draw time.
        return output;
    }

    /**
     * Shared margin pipeline. Expands the grid, re-renders
     * the SDF with a wide falloff, applies the shift, then
     * compresses back to the default falloff.
     *
     * @param src              Source contour texture with valid SDF.
     * @param maxExpansionMm   Largest outward (positive) margin in mm.
     *                          Drives grid expansion. Negative or zero
     *                          means pure erosion (no expansion needed).
     * @param maxAbsMarginMm   Largest absolute margin in mm across all
     *                          directions. Drives the temporary falloff
     *                          width.
     * @param applyShift       Callback that transforms SDF pixels
     *                          into the shifted result. Receives
     *                          (texture, currentFalloffMm).
     * @returns New ContourTexture with the margin applied.
     */
    private static async applyMarginBase(
        src: ContourTexture,
        maxExpansionMm: number,
        maxAbsMarginMm: number,
        applyShift: (
            texture: ContourTexture,
            falloffMm: number
        ) => Uint8Array[] | Promise<Uint8Array[]>,
        widenFalloff: boolean = true
    ): Promise<ContourTexture> {
        if (!src.signedDistanceField) {
            throw new Error('Cannot apply margin: source has no SDF data');
        }

        if (src.signedDistanceField.toArray().length === 0) {
            throw new Error('Cannot apply margin: source SDF is empty');
        }

        const falloffMm = ContourTexture.DEFAULT_MARGIN;
        const renderFalloffMm = widenFalloff
            ? maxAbsMarginMm + falloffMm
            : falloffMm;

        const expanded = ContourTexture.createNewWithMargin(
            maxExpansionMm,
            src
        );

        const renderer = RenderStorage.getRenderer(SDFOperationRenderer);
        renderer.renderOnDestination(expanded, BooleanOperation.OR, src);

        const sdfGenerator = RenderStorage.getRenderer(SdfGenerator);
        const pixels = await sdfGenerator.renderSDFFromTexture(
            expanded,
            renderFalloffMm,
            renderFalloffMm,
            true
        );

        expanded.resetSdfAndGrid(pixels, expanded.textureGrid);
        const shifted = await applyShift(expanded, renderFalloffMm);

        const outArrays = widenFalloff
            ? SDFOperations.decreaseFalloff(shifted, renderFalloffMm, falloffMm)
            : shifted;

        expanded.resetSdfAndGrid(outArrays, expanded.textureGrid);
        expanded.cropToImageBounds();
        return expanded;
    }

    /**
     * Apply Gaussian smoothing to an SDF texture.
     * Smoothing is performed in-place on a copy of the SDF data.
     *
     * @param src Source ContourTexture containing the SDF to smooth
     * @param level Smoothing level (1-5), where higher values produce stronger smoothing
     * @param iterations Number of smoothing iterations (default: 1)
     * @returns New ContourTexture with smoothed SDF, or undefined if source has no SDF
     */
    public static applyGaussianSmoothing(
        src: ContourTexture,
        level: number,
        iterations: number = 1
    ): ContourTexture | undefined {
        const extracted = SDFOperations.extractSdfPixelData(src);
        if (!extracted) {
            return undefined;
        }

        const { srcPixels, nx, ny, nz, threshold, falloffMm } = extracted;

        const sigma = getSigmaForLevel(level);

        const smoothedU8 = gaussianSmooth3DUint8(
            srcPixels,
            nx,
            ny,
            nz,
            threshold,
            falloffMm,
            threshold,
            sigma,
            iterations
        );

        return src.cloneWithNewSdf(smoothedU8);
    }

    /**
     * Apply morphological closing (dilate then erode) to smooth surfaces
     * while preserving volume. This avoids the shrinkage that Gaussian
     * smoothing causes on SDF structures.
     *
     * @param src Source ContourTexture containing the SDF
     * @param level Smoothing level (1-5), mapped to closing radius in mm
     * @returns New ContourTexture with closing applied, or undefined if source has no SDF
     */
    public static async applyMorphologicalClosing(
        src: ContourTexture,
        level: number
    ): Promise<ContourTexture | undefined> {
        if (!GlOperationGuard.acquire('Morphological closing')) {
            return undefined;
        }
        try {
            const extracted = SDFOperations.extractSdfPixelData(src);
            if (!extracted) {
                return undefined;
            }

            const { srcPixels, nx, ny, nz, threshold, falloffMm } = extracted;
            const radiusMm = getClosingRadiusForLevel(level);

            // Steps 1+2: convert metric Uint8 SDF to mm and dilate
            // in one pass. The input SDF is already metric, so we
            // just reverse the quantization formula instead of
            // running the expensive FMM reinitialization.
            const sdfMmDilated = SDFOperations.dequantizeUint8ToMm(
                srcPixels,
                nx,
                ny,
                nz,
                threshold,
                falloffMm,
                radiusMm
            );

            // Step 3: quantize back to Uint8 after dilation
            const dilatedU8 = quantizeSdfMmToUint8(
                sdfMmDilated,
                nx,
                ny,
                nz,
                threshold,
                falloffMm
            );

            // Step 3b: Gaussian smooth the dilated shape to remove
            // jaggedness. Smoothing is applied in the dilated state
            // so that any shrinkage it introduces is compensated by
            // the subsequent erosion step.
            const sigma = getSigmaForLevel(level);
            const smoothedU8 = gaussianSmooth3DUint8(
                dilatedU8,
                nx,
                ny,
                nz,
                threshold,
                falloffMm,
                threshold,
                sigma,
                1
            );

            // Yield to UI
            await new Promise((resolve) => setTimeout(resolve, 0));

            // Step 4: reinitialize SDF after dilation + smoothing
            // (WebGL-based). Pass smoothedU8 directly as CPU pixel
            // data to skip Phase 1 GPU readback. A blank
            // ContourTexture serves as the GPU write target.
            const writeTarget = new ContourTexture(
                src.grid,
                src.sizePx,
                src.textureGrid.getPosition(),
                src.margin
            );
            const sdfGenerator = new SdfGenerator();
            const reinitU8 = await sdfGenerator.renderSDFFromPixelData(
                writeTarget,
                falloffMm,
                falloffMm,
                smoothedU8,
                true // fullDistanceField: erosion needs true 3D distance
            );
            sdfGenerator.delete();
            writeTarget.delete();

            // Step 5: convert reinitialized Uint8 → mm and erode
            // (contract by radiusMm)
            const sdfMmEroded = SDFOperations.dequantizeUint8ToMm(
                reinitU8,
                nx,
                ny,
                nz,
                threshold,
                falloffMm,
                -radiusMm
            );

            // Step 6: quantize to final Uint8
            const outU8 = quantizeSdfMmToUint8(
                sdfMmEroded,
                nx,
                ny,
                nz,
                threshold,
                falloffMm
            );

            return src.cloneWithNewSdf(outU8);
        } finally {
            GlOperationGuard.release();
        }
    }

    /**
     * Remove small disconnected parts from an SDF texture.
     * Parts with volume below the threshold are removed.
     *
     * @param src Source ContourTexture
     * @param minVolumeCm3 Minimum volume to keep in cm³ (default: 0.5)
     * @param signal Optional AbortSignal for cancellation
     * @returns Object with texture and stats, or undefined if source has no SDF
     */
    public static async applyRemoveSmallParts(
        src: ContourTexture,
        minVolumeCm3: number = 0.5,
        signal?: AbortSignal
    ): Promise<
        | {
              texture: ContourTexture;
              numComponentsFound: number;
              numComponentsRemoved: number;
          }
        | undefined
    > {
        if (!GlOperationGuard.acquire('Remove small parts')) {
            return undefined;
        }
        try {
            const extracted = SDFOperations.extractSdfPixelData(src);
            if (!extracted) {
                return undefined;
            }

            const { srcPixels, nx, ny, nz, threshold } = extracted;

            // Calculate voxel volume in mm³
            const voxelVolumeMm3 =
                src.textureGrid.resX *
                src.textureGrid.resY *
                src.textureGrid.resZ;

            const { sdfU8, numComponentsFound, numComponentsRemoved } =
                await removeSmallParts(
                    srcPixels,
                    nx,
                    ny,
                    nz,
                    threshold,
                    voxelVolumeMm3,
                    minVolumeCm3,
                    signal
                );

            const texture = await SDFOperations.gpuReinitAndClone(sdfU8, src);

            return {
                texture,
                numComponentsFound,
                numComponentsRemoved,
            };
        } finally {
            GlOperationGuard.release();
        }
    }

    /**
     * Fill cavities (holes) in an SDF texture.
     * Any enclosed cavity inside the structure will be filled.
     *
     * @param src Source ContourTexture
     * @param signal Optional AbortSignal for cancellation
     * @returns New ContourTexture with cavities filled, or undefined if source has no SDF
     */
    public static async applyFillCavities(
        src: ContourTexture,
        signal?: AbortSignal
    ): Promise<ContourTexture | undefined> {
        if (!GlOperationGuard.acquire('Fill cavities')) {
            return undefined;
        }
        try {
            const extracted = SDFOperations.extractSdfPixelData(src);
            if (!extracted) {
                return undefined;
            }

            const { srcPixels, nx, ny, nz, threshold } = extracted;

            const processedU8 = await fillCavities(
                srcPixels,
                nx,
                ny,
                nz,
                threshold,
                signal
            );

            return SDFOperations.gpuReinitAndClone(processedU8, src);
        } finally {
            GlOperationGuard.release();
        }
    }

    /**
     * Reinitialize an existing Uint8 SDF using the WebGL-based SDFMesh pipeline.
     * Extracts iso-contours via marching squares and renders them through the GPU
     * SDF pipeline, producing smoother distance gradients than CPU-based FMM.
     *
     * @param src Source ContourTexture containing the SDF to reinitialize
     * @returns New ContourTexture with reinitialized SDF, or undefined if source has no SDF
     */
    public static async reinitializeSDFWebGL(
        src: ContourTexture
    ): Promise<ContourTexture | undefined> {
        if (!src.signedDistanceField) {
            return undefined;
        }

        // Acquire the GL guard if not already held by the caller
        // (e.g. applyGaussianSmoothingGL already holds it).
        const needsGuard = !GlOperationGuard.isActive;
        if (needsGuard && !GlOperationGuard.acquire('SDF reinitialization')) {
            return undefined;
        }
        try {
            // Read src pixels via the caching accessor (free on
            // subsequent calls).
            return await SDFOperations.gpuReinitAndClone(src.pixels, src);
        } finally {
            if (needsGuard) {
                GlOperationGuard.release();
            }
        }
    }

    /**
     * Guard + extract SDF pixel data and grid dimensions from a ContourTexture.
     * Returns undefined if the source has no valid SDF data.
     */
    private static extractSdfPixelData(src: ContourTexture):
        | {
              srcPixels: Uint8Array[];
              nx: number;
              ny: number;
              nz: number;
              threshold: number;
              falloffMm: number;
          }
        | undefined {
        if (!src.signedDistanceField) {
            return undefined;
        }

        const srcPixels = src.signedDistanceField.toArray();
        if (!srcPixels) {
            return undefined;
        }

        return {
            srcPixels,
            nx: src.textureGrid.sizeX,
            ny: src.textureGrid.sizeY,
            nz: src.textureGrid.sizeZ,
            threshold: ContourTexture.SDF_THRESHOLD,
            falloffMm: ContourTexture.DEFAULT_MARGIN,
        };
    }

    /**
     * Convert per-slice Uint8Array[] SDF to a flat Float32Array of signed
     * distances in mm, with an offset applied. Reverses the standard
     * quantization formula: mm = (value - threshold) * (falloffMm / 127) + offsetMm
     */
    private static dequantizeUint8ToMm(
        srcPixels: Uint8Array[],
        nx: number,
        ny: number,
        nz: number,
        threshold: number,
        falloffMm: number,
        offsetMm: number
    ): Float32Array {
        const plane = nx * ny;
        const mmPerUnit = falloffMm / 127.0;
        const sdfMm = new Float32Array(plane * nz);
        for (let z = 0; z < nz; z++) {
            const off = z * plane;
            const slice = srcPixels[z];
            for (let i = 0; i < plane; i++) {
                sdfMm[off + i] = (slice[i] - threshold) * mmPerUnit + offsetMm;
            }
        }
        return sdfMm;
    }

    /**
     * Reinitialize a modified SDF via the GPU pipeline and return a new
     * ContourTexture cloned from the source with the reinitialized SDF.
     */
    private static async gpuReinitAndClone(
        processedU8: Uint8Array[],
        src: ContourTexture
    ): Promise<ContourTexture> {
        const falloffMm = ContourTexture.DEFAULT_MARGIN;
        const writeTarget = new ContourTexture(
            src.grid,
            src.sizePx,
            src.textureGrid.getPosition(),
            src.margin
        );
        const sdfGenerator = new SdfGenerator();
        const reinitializedU8 = await sdfGenerator.renderSDFFromPixelData(
            writeTarget,
            falloffMm,
            falloffMm,
            processedU8,
            true // fullDistanceField: geometry ops need true 3D distance
        );
        sdfGenerator.delete();
        writeTarget.delete();

        return src.cloneWithNewSdf(reinitializedU8);
    }

    /**
     * Shift an SDF by a constant margin, preserving the falloff encoding.
     * Note that this will also shift voxels far away from the surface so applying
     * multiple margins sequentially will eventually invalidate the sdf boundary.
     *
     * @param pixels SDF slices encoded with {@link falloffMm}
     * @param marginMm Expansion (positive) or erosion (negative) in mm
     * @param falloffMm Falloff distance of the encoding
     * @returns Shifted SDF slices in the same encoding
     */
    private static applySymmetricShift(
        pixels: Uint8Array[],
        marginMm: number,
        falloffMm: number = ContourTexture.DEFAULT_MARGIN
    ): Uint8Array[] {
        const delta = marginMm * (ContourTexture.SDF_THRESHOLD / falloffMm);
        const outArrays = new Array<Uint8Array>(pixels.length);

        for (let z = 0; z < pixels.length; z++) {
            const slice = pixels[z];
            const outSlice = new Uint8Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                const val = slice[i] + delta;
                outSlice[i] = val < 0 ? 0 : val > 255 ? 255 : Math.round(val);
            }
            outArrays[z] = outSlice;
        }

        return outArrays;
    }

    /**
     * Re-encode an SDF from a larger falloff to a smaller falloff.
     *
     * @param pixels SDF slices encoded with {@link inputFalloffMm}
     * @param inputFalloffMm Falloff distance of the input encoding
     * @param outputFalloffMm Falloff distance for the output encoding. Must be smaller than {@link inputFalloffMm}
     * @returns SDF slices re-encoded with {@link outputFalloffMm}
     */
    private static decreaseFalloff(
        pixels: Uint8Array[],
        inputFalloffMm: number,
        outputFalloffMm: number
    ): Uint8Array[] {
        if (inputFalloffMm < outputFalloffMm) {
            throw new Error(
                `Input falloff (${inputFalloffMm}mm) must be greater than output falloff (${outputFalloffMm}mm)`
            );
        }
        const threshold = ContourTexture.SDF_THRESHOLD;
        const scale = inputFalloffMm / outputFalloffMm;
        const outArrays = new Array<Uint8Array>(pixels.length);

        for (let z = 0; z < pixels.length; z++) {
            const slice = pixels[z];
            const outSlice = new Uint8Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                const val = threshold + (slice[i] - threshold) * scale;
                outSlice[i] = val < 0 ? 0 : val > 255 ? 255 : Math.round(val);
            }
            outArrays[z] = outSlice;
        }

        return outArrays;
    }
}

/** Map smoothing level (1-5) to morphological closing radius in mm. */
function getClosingRadiusForLevel(level: number): number {
    switch (level) {
        case 1:
            return 1.0;
        case 2:
            return 2.0;
        case 3:
            return 3.0;
        case 4:
            return 5.0;
        case 5:
            return 7.0;
        default:
            return 3.0;
    }
}
